/**
 * The caller's block list — server-backed, shared across their devices.
 *
 *   const { blocked, isBlocked, block, unblock, isLoading } = useBlockedContacts()
 *
 * Loaded once on mount and kept in local state; `block`/`unblock` update
 * optimistically and roll back on failure, so the settings list and the
 * conversation banner react instantly.
 *
 * The initial load retries on failure (`blockedRetryDelay`) instead of giving
 * up after one try: `presence.store`'s `blockedReady` flag holds every other
 * user's presence as unknown until this load succeeds at least once, so a
 * load that silently stayed failed would hide presence for the whole page for
 * the rest of the session. After three failed attempts the store is told to
 * stop holding (fail open) so the page is usable, and the retries continue
 * quietly in the background at a fixed interval.
 *
 * Enforcement is entirely server-side (silent DM drop, no call ring, presence
 * hidden both ways) — this hook exists so the UI can REFLECT the block, never to
 * implement it. A client that skipped every check here would still be blocked.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { usePresenceStore } from '@/features/presence/store/presence.store'
import {
  blockContact,
  listBlockedContacts,
  unblockContact,
} from '@/sdk/instavcMessenger-instance'
import type { BlockedContact } from '@/sdk/instavcMessenger'

/** Attempts before the store is told to fail open and stop holding presence. */
const FAIL_OPEN_AFTER_ATTEMPTS = 3
/** Delay before every retry past the fail-open point. */
const STEADY_RETRY_MS = 15_000

/**
 * Delay, in ms, before the retry that follows the attempt numbered `attempt`
 * (0-indexed: `attempt` 0 is the delay after the FIRST failure). Backs off
 * 1 s, 2 s, 4 s, then holds at a flat interval for as long as the page stays
 * open and failing.
 */
export function blockedRetryDelay(attempt: number): number {
  const schedule = [1_000, 2_000, 4_000]
  return schedule[attempt] ?? STEADY_RETRY_MS
}

interface UseBlockedContacts {
  blocked: BlockedContact[]
  /** Is this contactPresenceId blocked by me? */
  isBlocked: (userId: string) => boolean
  block: (userId: string, name?: string | null) => Promise<void>
  unblock: (userId: string) => Promise<void>
  isLoading: boolean
  error: string | null
}

export function useBlockedContacts(): UseBlockedContacts {
  const me = useAuthStore((s) => s.user)
  // Block endpoints key on the caller's contactPresenceId server-side, but the
  // SDK instance is built from the auth user id — same as every other REST call.
  const presenceId = useAuthStore((s) => s.presenceId)
  const [blocked, setBlocked] = useState<BlockedContact[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!me?.id || !presenceId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0
    let failedOpen = false

    const userId = me.id

    const load = () => {
      setIsLoading(true)
      listBlockedContacts(userId)
        .then((rows) => {
          if (cancelled) return
          setBlocked(rows)
          setError(null)
        })
        .catch(() => {
          if (cancelled) return
          setError('failed to load blocked contacts')
          // Fail open once the budget runs out: presence must not stay held
          // for the whole session over a block list that keeps failing. The
          // store's own set is left as-is (likely still empty) and retries
          // keep going underneath.
          if (!failedOpen && attempt + 1 >= FAIL_OPEN_AFTER_ATTEMPTS) {
            failedOpen = true
            usePresenceStore.getState().markBlockedReadyFailOpen()
            console.warn(
              '[blocked-contacts] blocked list failed to load after',
              FAIL_OPEN_AFTER_ATTEMPTS,
              'attempts, no longer holding presence; retrying in the background',
            )
          }
          const delay = blockedRetryDelay(attempt)
          attempt += 1
          timer = setTimeout(load, delay)
        })
        .finally(() => { if (!cancelled) setIsLoading(false) })
    }

    load()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [me?.id, presenceId])

  const blockedIds = useMemo(
    () => new Set(blocked.map((b) => b.userId)),
    [blocked],
  )

  // Keep presence in step with the block list.
  //
  // Blocking makes the server stop sending that user's presence, which means
  // the last status it *did* send — usually `online`, since you tend to block
  // someone you were just talking to — stayed on screen with nothing ever
  // arriving to replace it. Driving it from `blocked` rather than from the
  // block() call covers the initial load, the optimistic add, a failed block
  // rolling back, and unblock restoring them, all through one path.
  //
  // The very first run of this effect fires on mount, before `load` above has
  // had any chance to resolve: `blocked` is still its initial `[]`. Pushing
  // that placeholder would mark the presence store's `blockedReady` true
  // immediately, defeating the hold this hook exists to drive. `load`'s own
  // result (success or fail-open) is what should decide readiness, so this
  // skips exactly that one synthetic pass and runs normally after.
  const isFirstBlockedSync = useRef(true)
  useEffect(() => {
    if (isFirstBlockedSync.current) {
      isFirstBlockedSync.current = false
      return
    }
    usePresenceStore.getState().setBlockedIds([...blockedIds])
  }, [blockedIds])

  const isBlocked = useCallback((userId: string) => blockedIds.has(userId), [blockedIds])

  const block = useCallback(
    async (userId: string, name: string | null = null) => {
      if (!me?.id) return
      // Optimistic: the banner + composer lock should land on the click, not a
      // round trip later.
      const optimistic: BlockedContact = {
        userId,
        name,
        avatarUrl: null,
        blockedAt: new Date().toISOString(),
      }
      setBlocked((prev) => (prev.some((b) => b.userId === userId) ? prev : [optimistic, ...prev]))
      setError(null)
      try {
        await blockContact(me.id, userId)
      } catch {
        setBlocked((prev) => prev.filter((b) => b.userId !== userId))
        setError('failed to block')
      }
    },
    [me?.id],
  )

  const unblock = useCallback(
    async (userId: string) => {
      if (!me?.id) return
      const previous = blocked
      setBlocked((prev) => prev.filter((b) => b.userId !== userId))
      setError(null)
      try {
        await unblockContact(me.id, userId)
      } catch {
        setBlocked(previous)
        setError('failed to unblock')
      }
    },
    [me?.id, blocked],
  )

  return { blocked, isBlocked, block, unblock, isLoading, error }
}
